"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCart } from "../context/CartContext";
import { formatCurrency } from "../utils/currency";
import { handleMediaError, resolveMediaUrl } from "../utils/media";
import { ShoppingCart, Star } from "lucide-react";
import toast from "react-hot-toast";
import { getDiscountRibbonText, getPrimaryProductImage, getProductPath, getProductPricing, StoreProduct } from "../utils/product";

interface ProductCardProps {
  product: StoreProduct;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addToCart } = useCart();
  const router = useRouter();
  const pricing = getProductPricing(product);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.variants?.length) {
      router.push(getProductPath(product));
      toast("Choose a color before adding this product.");
      return;
    }
    if (product.stock > 0) {
      addToCart(product, 1);
      toast.success(`${product.name} added to cart!`);
    } else {
      toast.error("Sorry, this item is out of stock.");
    }
  };

  // Safe fallback image
  const displayImage = resolveMediaUrl(getPrimaryProductImage(product)) || "https://images.unsplash.com/photo-1546868871-7041f2a55e12?q=80&w=300&auto=format&fit=crop";

  return (
    <Link
      href={getProductPath(product)}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-4 transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/5 hover:border-indigo-500/50"
    >
      {/* Product Image Area */}
      <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-zinc-100 dark:bg-zinc-900 group-hover:opacity-90 transition-opacity">
        <img
          src={displayImage}
          alt={product.name}
          loading="lazy"
          decoding="async"
          onError={handleMediaError}
          className="h-full w-full object-contain p-2 object-center transition-transform duration-500 group-hover:scale-105"
        />
        {/* Featured Tag */}
        {product.isFeatured && (
          <span className="absolute top-2.5 left-2.5 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 px-2.5 py-1 text-[10px] font-semibold text-white tracking-wide uppercase shadow-sm">
            Featured
          </span>
        )}
        {product.showDiscountRibbon && (
          <span className="absolute right-2.5 top-2.5 max-w-[70%] rounded bg-red-600 px-2.5 py-1 text-[10px] font-black uppercase text-white shadow-sm">
            {getDiscountRibbonText(product)}
          </span>
        )}
        {/* Out of Stock Overlay */}
        {product.stock <= 0 && (
          <div className="absolute inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center">
            <span className="rounded-md bg-rose-600 px-3 py-1 text-xs font-bold text-white tracking-wide uppercase">
              Sold Out
            </span>
          </div>
        )}
      </div>

      {/* Product Details */}
      <div className="mt-4 flex flex-col flex-1">
        <div className="flex items-center gap-1.5 text-xs text-zinc-400 dark:text-zinc-500 font-semibold tracking-wider uppercase">
          <span>{product.category}</span>
        </div>
        
        <h3 className="mt-1.5 text-sm font-semibold text-zinc-900 dark:text-zinc-100 line-clamp-2 min-h-[40px] group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors">
          {product.name}
        </h3>

        {/* Rating Mock */}
        <div className="mt-2 flex items-center gap-1">
          <div className="flex text-amber-500">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-3.5 w-3.5 fill-current" />
            ))}
          </div>
          <span className="text-[11px] font-medium text-zinc-400 dark:text-zinc-500">(5.0)</span>
        </div>

        {/* Price & Cart Trigger */}
        <div className="mt-auto pt-4 flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs text-zinc-400 dark:text-zinc-500">Price</span>
            {pricing.hasDiscount ? (
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-xs text-zinc-400 line-through">{formatCurrency(pricing.regularPrice)}</span>
                <span className="text-lg font-bold text-zinc-900 dark:text-zinc-50 tracking-tight">{formatCurrency(pricing.discountedPrice)}</span>
              </div>
            ) : (
              <span className="text-lg font-bold text-zinc-900 dark:text-zinc-50 tracking-tight">{formatCurrency(pricing.payablePrice)}</span>
            )}
          </div>

          <button
            onClick={handleAddToCart}
            disabled={product.stock <= 0}
            className={`flex h-10 w-10 items-center justify-center rounded-full transition-all ${
              product.stock > 0
                ? "bg-zinc-950 hover:bg-violet-600 dark:bg-zinc-100 dark:hover:bg-violet-500 text-white dark:text-zinc-950 hover:text-white shadow-md active:scale-95"
                : "bg-zinc-100 dark:bg-zinc-900 text-zinc-300 dark:text-zinc-700 cursor-not-allowed"
            }`}
            title="Add to Cart"
          >
            <ShoppingCart className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Link>
  );
};
export default ProductCard;
